import {CommonModule} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {PaymentProvider} from '@gatewee/types/provider';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';

interface DialogData {
  id: number;
  submitCredentialsFn: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider: PaymentProvider;
}

@Component({
  selector: 'app-paytime-credentials',
  templateUrl: './paytime-credentials.component.html',
  styleUrls: ['./paytime-credentials.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
  ],
})
export class PaytimeCredentialsComponent {
  form: FormGroup;
  submitting = false;
  id!: number;
  submitCredentialsFn!: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider!: PaymentProvider;

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.submitCredentialsFn = this.context.data.submitCredentialsFn;
    this.provider = this.context.data.provider;
    this.form = this.fb.group({
      advanced: [false],
      sandbox: [false],
      'integration-key': [''],
      'x-token': [''],
      'authentication-key': [''],
      establishmentId: [null, [Validators.required]],
    });

    // Set up initial validators (only establishmentId is required)
    this.updateValidators();

    // Watch for advanced mode changes
    this.form.get('advanced')?.valueChanges.subscribe(() => {
      this.updateValidators();
    });
  }
  private updateValidators() {
    const advanced = this.form.get('advanced')?.value;
    const sandboxControl = this.form.get('sandbox');
    const integrationKeyControl = this.form.get('integration-key');
    const xTokenControl = this.form.get('x-token');
    const authKeyControl = this.form.get('authentication-key');
    const establishmentIdControl = this.form.get('establishmentId');

    if (advanced) {
      // Advanced mode: all fields required
      sandboxControl?.setValidators([Validators.required]);
      integrationKeyControl?.setValidators([
        Validators.required,
        Validators.minLength(3),
      ]);
      xTokenControl?.setValidators([
        Validators.required,
        Validators.minLength(3),
      ]);
      authKeyControl?.setValidators([
        Validators.required,
        Validators.minLength(3),
      ]);
      establishmentIdControl?.setValidators([Validators.required]);
    } else {
      // Simple mode: only establishmentId required
      sandboxControl?.clearValidators();
      integrationKeyControl?.clearValidators();
      xTokenControl?.clearValidators();
      authKeyControl?.clearValidators();
      establishmentIdControl?.setValidators([Validators.required]);
    }

    // Update validity
    sandboxControl?.updateValueAndValidity({emitEvent: false});
    integrationKeyControl?.updateValueAndValidity({emitEvent: false});
    xTokenControl?.updateValueAndValidity({emitEvent: false});
    authKeyControl?.updateValueAndValidity({emitEvent: false});
    establishmentIdControl?.updateValueAndValidity({emitEvent: false});
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;

    const formValue = this.form.value;
    const payload = {
      sandbox: formValue.sandbox || false,
      'integration-key': formValue['integration-key'] || '',
      'x-token': formValue['x-token'] || '',
      'authentication-key': formValue['authentication-key'] || '',
      establishmentId: formValue.establishmentId,
    };

    let handledSuccess = false;
    const handleSuccess = () => {
      if (handledSuccess) return;
      handledSuccess = true;
      this.alerts
        .open(
          this.translate.instant(
            'features.integrations.credentials.success.message',
          ),
          {
            label: this.translate.instant(
              'features.integrations.credentials.success.title',
            ),
            appearance: 'success',
          },
        )
        .subscribe();
      this.context.completeWith(true);
    };

    this.submitCredentialsFn(this.provider, payload).subscribe({
      next: () => handleSuccess(),
      complete: () => handleSuccess(),
      error: (error: Error) => {
        console.error('Error creating API key:', error);
        this.alerts
          .open(
            this.translate.instant(
              'features.integrations.credentials.error.message',
            ),
            {
              label: this.translate.instant(
                'features.integrations.credentials.error.title',
              ),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close() {
    this.context.completeWith(false);
  }
}
